import React, { useState, useEffect, useCallback } from 'react';
import BibleVerseCard from '../components/BibleVerseCard';
import ConfirmModal from '../components/ConfirmModal';
import './Calculate.css';

// Pure function: compute consumption from readings and tenant
const computeConsumption = (readings, tenant) => {
  let electricityConsumed = 0;
  let waterConsumed = 0;

  if (readings.currElectricityMain && readings.prevElectricityMain) {
    if (tenant?.has_esm61_meter && readings.currElectricityEsm61 && readings.prevElectricityEsm61) {
      const mainConsumption = parseFloat(readings.currElectricityMain) - parseFloat(readings.prevElectricityMain);
      const esm61Consumption = parseFloat(readings.currElectricityEsm61) - parseFloat(readings.prevElectricityEsm61);
      electricityConsumed = mainConsumption - esm61Consumption;
    } else {
      electricityConsumed = parseFloat(readings.currElectricityMain) - parseFloat(readings.prevElectricityMain);
    }
  }

  if (readings.currWater && readings.prevWater) {
    waterConsumed = parseFloat(readings.currWater) - parseFloat(readings.prevWater);
  }

  return { electricityConsumed, waterConsumed };
};

const Calculate = ({ bibleVerse }) => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('');
  const [readings, setReadings] = useState({
    prevElectricityMain: '',
    currElectricityMain: '',
    prevElectricityEsm61: '',
    currElectricityEsm61: '',
    prevWater: '',
    currWater: ''
  });
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [billedTenantIds, setBilledTenantIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'confirm' });

  useEffect(() => {
    fetchTenants();
    initializeBillingPeriod();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchLastMonthData();
    }
  }, [selectedTenant]);

  // Real-time consumption preview — runs after state commits, always current
  useEffect(() => {
    const tenant = tenants.find(t => t.id === parseInt(selectedTenant));
    if (!tenant) return;

    const { electricityConsumed, waterConsumed } = computeConsumption(readings, tenant);

    if (readings.currElectricityMain || readings.currWater) {
      setCalculation({
        electricityConsumed: electricityConsumed.toFixed(1),
        waterConsumed: waterConsumed.toFixed(1)
      });
    }
  }, [readings, selectedTenant, tenants]);

  // Fetch billing status when period changes
  const fetchBillingStatus = useCallback(async (period) => {
    try {
      const response = await fetch(`/api/get-billing-status?period=${period}`);
      const result = await response.json();
      if (result.success) {
        setBilledTenantIds(result.data.billed_tenant_ids || []);
      }
    } catch (error) {
      // Silently fail — dropdown works normally without indicator
    }
  }, []);

  useEffect(() => {
    if (billingPeriod) {
      fetchBillingStatus(billingPeriod);
    }
  }, [billingPeriod, fetchBillingStatus]);

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/get-tenants');
      const result = await response.json();
      if (result.success) {
        setTenants(result.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const initializeBillingPeriod = () => {
    const now = new Date();
    const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setBillingPeriod(period);
  };

  const fetchLastMonthData = async () => {
    try {
      const response = await fetch(`/api/get-last-month-data/${selectedTenant}`);
      const result = await response.json();
      if (result.success && result.data) {
        setReadings(prev => ({
          ...prev,
          prevElectricityMain: result.data.electricity_main || '',
          prevElectricityEsm61: result.data.electricity_esm61 || '',
          prevWater: result.data.water || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching last month data:', error);
    }
  };

  const handleReadingChange = (field, value) => {
    setReadings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const downloadPDF = async (billId) => {
    try {
      const response = await fetch(`/api/download-pdf/${billId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `utility_bill_${billId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleCalculate = async () => {
    try {
      const tenant = tenants.find(t => t.id === parseInt(selectedTenant));
      if (!tenant) {
        throw new Error('Please select a tenant');
      }

      const { electricityConsumed, waterConsumed } = computeConsumption(readings, tenant);

      const confirmMessage = `Tenant: ${tenant.name}\nPeriod: ${billingPeriod}\nElectricity: ${electricityConsumed.toFixed(1)} kWh\nWater: ${waterConsumed.toFixed(1)} m\u00B3\n\nProceed with calculation?`;

      setConfirmModal({
        isOpen: true,
        title: 'Confirm Bill Calculation',
        message: confirmMessage,
        type: 'confirm',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          doCalculate(tenant);
        }
      });
    } catch (error) {
      setError(error.message);
    }
  };

  const doCalculate = async (tenant) => {
    try {
      setLoading(true);
      setLoadingMessage('Calculating bill and generating PDF...');
      setError('');
      setSuccess('');

      const requestData = {
        tenant_id: parseInt(selectedTenant),
        billing_period: billingPeriod,
        prev_electricity_main: parseFloat(readings.prevElectricityMain),
        curr_electricity_main: parseFloat(readings.currElectricityMain),
        prev_water: parseFloat(readings.prevWater),
        curr_water: parseFloat(readings.currWater)
      };

      if (tenant.has_esm61_meter) {
        requestData.prev_electricity_esm61 = parseFloat(readings.prevElectricityEsm61);
        requestData.curr_electricity_esm61 = parseFloat(readings.currElectricityEsm61);
      }

      const response = await fetch('/api/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Bill calculated successfully!');
        setCalculation({
          electricityConsumed: result.data.electricity_consumed.toFixed(1),
          waterConsumed: result.data.water_consumed.toFixed(1),
          electricityCost: result.data.electricity_cost.toFixed(2),
          waterCost: result.data.water_cost.toFixed(2),
          totalCost: result.data.total_cost.toFixed(2),
          billId: result.data.bill_id,
          pdfPath: result.data.pdf_path
        });

        // Auto-download PDF if available
        if (result.data.bill_id) {
          setTimeout(() => {
            downloadPDF(result.data.bill_id);
          }, 1000);
        }

        // Refresh billing status to show checkmark
        if (billingPeriod) {
          fetchBillingStatus(billingPeriod);
        }
      } else {
        throw new Error(result.error || 'Calculation failed');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const selectedTenantData = tenants.find(t => t.id === parseInt(selectedTenant));

  return (
    <div className="calculate-page">
      <h2>Calculate Utility Bill</h2>

      <BibleVerseCard verse={bibleVerse} />

      <div className="calculate-form card">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tenant</label>
            <select
              className="form-select"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
            >
              <option value="">Select a tenant</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} {tenant.has_esm61_meter ? '(ESM61)' : ''}{billedTenantIds.includes(tenant.id) ? ' \u2713' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Billing Period</label>
            <input
              type="month"
              className="form-input"
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
            />
          </div>
        </div>

        {selectedTenant && (
          <>
            <h3>Electricity Readings</h3>
            <div className="readings-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Previous Main Reading (kWh)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-input"
                    value={readings.prevElectricityMain}
                    onChange={(e) => handleReadingChange('prevElectricityMain', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Current Main Reading (kWh)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-input"
                    value={readings.currElectricityMain}
                    onChange={(e) => handleReadingChange('currElectricityMain', e.target.value)}
                  />
                </div>
              </div>

              {selectedTenantData?.has_esm61_meter && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Previous ESM61 Reading (kWh)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-input"
                      value={readings.prevElectricityEsm61}
                      onChange={(e) => handleReadingChange('prevElectricityEsm61', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Current ESM61 Reading (kWh)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-input"
                      value={readings.currElectricityEsm61}
                      onChange={(e) => handleReadingChange('currElectricityEsm61', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <h3>Water Readings</h3>
            <div className="readings-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Previous Water Reading (m³)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-input"
                    value={readings.prevWater}
                    onChange={(e) => handleReadingChange('prevWater', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Current Water Reading (m³)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-input"
                    value={readings.currWater}
                    onChange={(e) => handleReadingChange('currWater', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {calculation && (
              <div className="consumption-preview">
                <h4>Consumption Preview</h4>
                <div className="consumption-row">
                  <span>Electricity: {calculation.electricityConsumed} kWh</span>
                  <span>Water: {calculation.waterConsumed} m³</span>
                </div>
                {calculation.electricityCost && (
                  <div className="cost-summary">
                    <div className="cost-row">
                      <span>Electricity Cost:</span>
                      <span>FL {calculation.electricityCost}</span>
                    </div>
                    <div className="cost-row">
                      <span>Water Cost:</span>
                      <span>FL {calculation.waterCost}</span>
                    </div>
                    <div className="cost-row total">
                      <span>Total Cost:</span>
                      <span>FL {calculation.totalCost}</span>
                    </div>
                    {calculation.billId && (
                      <button
                        className="btn btn-success download-btn"
                        onClick={() => downloadPDF(calculation.billId)}
                      >
                        Download PDF
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              className="btn btn-primary calculate-btn"
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? <span className="loading"></span> : '\u{1F9EE}'}
              Calculate & Generate PDF
            </button>

            {loading && (
              <div className="loading-container mobile-loading">
                <span className="loading"></span>
                <span className="loading-text">{loadingMessage}</span>
              </div>
            )}
          </>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default Calculate;
